import { Alignment, ColumnComposition, ColumnCompositions } from '@blk/simba';
import { InjectConfig, ModelsExperienceConfigKey } from '@ddt-web/config-layer';
import { CellData, TabData, TableData } from '@ddt-web/data-service';
import { DefaultColumnComposition, TableCompositionHelper } from '@ddt-web/pdf-components';
import * as get from 'lodash/get';

import { HoldingsTableUtil } from '../holdings-table.util';

export class FamilyHoldingsHeaderColumnComposition extends DefaultColumnComposition {
	@InjectConfig(ModelsExperienceConfigKey.HOLDINGS_TABLE_HEADER_COLUMNS) private readonly holdingsTableHeaderColumns = ['expenseRatio', 'grossExpenseRatio'];
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_HOLDINGS_TABLE_HEADER_V2) private readonly enableHoldingsTableHeaderV2: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.ENABLE_PDF_HOLDINGS_HEADER_WRAPPING) private readonly enableHeaderWrapping: boolean = false;
	@InjectConfig(ModelsExperienceConfigKey.HIDE_LATEST_ALLOCATION_FAMILY_LIST) private readonly hideLatestAllocationFamilies = [];
	protected getModelHeaderRowCompositions(headers: Array<string>, columnProportions: Array<number>): Array<ColumnComposition> {
		return headers.map(
			(header: string, index: number) => new ColumnComposition({
				cells: CellData.create({ config: null, rawValue: header, formattedValue: header, parent: null, attribute: null, children: null }),
				widthProportion: columnProportions[index],
				textClasses: ['text-bold', 'text-8'],
				horizontalAlignment: Alignment.Horizontal.Right,
				verticalAlignment: Alignment.Vertical.Middle,
				boxModelConstraint: { paddingRight: TableCompositionHelper.getTableColumnBoxModelConstraints(headers.length - 2, true) }
			})
		);
	}

	public getModelColumnCompositions(
		tabData: TabData,
		modelNames: string[],
		asOfDate: string[],
		familyId: string,
		latestAllocationData?: string[]
	): ColumnCompositions {
		const tableData: TableData = get(tabData, 'tableData[0]');
		const updatedModelNames = this.enableHeaderWrapping ? HoldingsTableUtil.wrapModelNames(modelNames) : modelNames;
		const categories: Array<CellData> = get(tableData, 'headersY', []).filter(
			(header: CellData) => header.config && header.config.visible && this.holdingsTableHeaderColumns.includes(header.config.id)
		);
		const tableColumnProportions = TableCompositionHelper.getHoldingsColumnProportions(modelNames.length - 2, false, true);

		return new ColumnCompositions(
			this.getModelHeaderRowCompositions(updatedModelNames, tableColumnProportions),
			tableData.cells
				.filter((row: Array<CellData>) => get(row[0], 'config.visible') &&
					this.holdingsTableHeaderColumns.includes(row?.[0]?.config?.id) &&
					!(this.hideLatestAllocationFamilies.includes(familyId) && row?.[0]?.config?.id === 'latestAllocation')
				)
				.map((row: Array<CellData>, index: number) => {

					return [
						new ColumnComposition({
							cells: categories[index],
							widthProportion: tableColumnProportions[1] - 1,
							textClasses: [this.textSize, 'text-bold'],
							horizontalAlignment: Alignment.Horizontal.Left,
							verticalAlignment: Alignment.Vertical.Middle,
							boxModelConstraint: this.firstColumnBoxModel
						}),
						new ColumnComposition({
							cells: asOfDate[index],
							widthProportion: tableColumnProportions[0] + 1,
							textClasses: [this.textSize],
							horizontalAlignment: Alignment.Horizontal.Right,
							verticalAlignment: Alignment.Vertical.Middle,
							boxModelConstraint: { paddingRight: TableCompositionHelper.getTableColumnBoxModelConstraints(modelNames.length - 2, true) }
						}),

						...row.map(
							(cell: CellData, cellIndex: number) => new ColumnComposition({
								cells: this.enableHoldingsTableHeaderV2 && cell?.config?.id === 'latestAllocation' ? latestAllocationData?.[cellIndex] : cell,
								widthProportion: tableColumnProportions[cellIndex + 2],
								textClasses: [this.textSize],
								horizontalAlignment: Alignment.Horizontal.Right,
								verticalAlignment: Alignment.Vertical.Middle,
								boxModelConstraint: { paddingRight: TableCompositionHelper.getTableColumnBoxModelConstraints(modelNames.length - 2, true) }
							})
						)
					];
				})
		);
	}
}
